import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Briefcase, RefreshCw, Users } from 'lucide-react';
import { fetchJobs, fetchJobPipeline, type PipelineData } from '@/lib/api';
import { KanbanBoard } from '@/components/pipeline/KanbanBoard';
import { TopMatchesPanel } from '@/components/pipeline/TopMatchesPanel';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PIPELINE_STAGES, STATUS_LABELS, STATUS_COLORS, type JobStatus } from '@/types';

// Pipelines stay accessible for every job status — pausing or filling a job
// only affects public advertising, never CRM pipeline access. Open jobs are
// listed first in the selector.
const STATUS_SORT_ORDER: Record<JobStatus, number> = {
  open: 0,
  paused: 1,
  filled: 2,
  draft: 3,
  cancelled: 4,
};

// Create empty pipeline data structure
function createEmptyPipelineData(jobId: string): PipelineData {
  const stages: Record<string, never[]> = {};
  for (const stage of PIPELINE_STAGES) {
    stages[stage] = [];
  }
  return {
    jobId,
    jobTitle: '',
    stages,
    totalApplications: 0,
  };
}

export function PipelinePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const jobIdFromUrl = searchParams.get('jobId');

  const [selectedJobId, setSelectedJobId] = useState<string | null>(jobIdFromUrl);

  // Fetch jobs for the dropdown — all statuses, so pausing a job never
  // closes its pipeline (candidates and docs stay reachable).
  const { data: jobsData, isLoading: isLoadingJobs } = useQuery({
    queryKey: ['jobs', { limit: 100 }],
    queryFn: () => fetchJobs({ limit: 100 }),
  });

  // Auto-select first job if none selected
  useEffect(() => {
    if (!selectedJobId && jobsData?.data && jobsData.data.length > 0) {
      const firstJob = jobsData.data[0];
      setSelectedJobId(firstJob.id);
      setSearchParams({ jobId: firstJob.id });
    }
  }, [jobsData, selectedJobId, setSearchParams]);

  // Fetch pipeline data for selected job
  const {
    data: pipelineData,
    isLoading: isLoadingPipeline,
    refetch: refetchPipeline,
    isFetching,
  } = useQuery({
    queryKey: ['pipeline', selectedJobId],
    queryFn: () => fetchJobPipeline(selectedJobId!),
    enabled: !!selectedJobId,
  });

  // Handle job selection change
  const handleJobChange = (jobId: string) => {
    setSelectedJobId(jobId);
    setSearchParams({ jobId });
  };

  const jobs = [...(jobsData?.data || [])].sort(
    (a, b) =>
      (STATUS_SORT_ORDER[a.status as JobStatus] ?? 9) -
      (STATUS_SORT_ORDER[b.status as JobStatus] ?? 9),
  );
  const selectedJob = jobs.find((job) => job.id === selectedJobId);

  // Count total applications across all stages
  const totalApplications = pipelineData?.totalApplications || 0;

  return (
    <div className="flex flex-col h-full">
      {/* Page Header */}
      <div className="flex-shrink-0 pb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-text-primary">Pipeline</h1>
            <p className="text-text-secondary mt-1">
              Track and manage candidates through the hiring process
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Refresh Button */}
            <Button
              variant="outline"
              size="icon"
              onClick={() => refetchPipeline()}
              disabled={!selectedJobId || isFetching}
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            </Button>

            {/* Job Selector */}
            <div className="w-72">
              {isLoadingJobs ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <Select
                  value={selectedJobId || ''}
                  onValueChange={handleJobChange}
                >
                  <SelectTrigger>
                    <div className="flex items-center gap-2 truncate">
                      <Briefcase className="h-4 w-4 text-text-tertiary flex-shrink-0" />
                      <SelectValue placeholder="Select a job..." />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {jobs.length === 0 ? (
                      <div className="p-4 text-sm text-text-tertiary text-center">
                        No jobs available
                      </div>
                    ) : (
                      jobs.map((job) => (
                        <SelectItem key={job.id} value={job.id}>
                          <div className="flex items-center justify-between gap-4">
                            <span className="truncate">{job.title}</span>
                            <span className="flex items-center gap-2">
                              {job.status !== 'open' && (
                                <span
                                  className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${STATUS_COLORS[job.status as JobStatus] ?? ''}`}
                                >
                                  {STATUS_LABELS[job.status as JobStatus] ?? job.status}
                                </span>
                              )}
                              {job.company && (
                                <span className="text-xs text-text-tertiary">
                                  {job.company.name}
                                </span>
                              )}
                            </span>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </div>

        {/* Pipeline Stats */}
        {selectedJob && (
          <div className="mt-4 flex items-center gap-4 text-sm text-text-tertiary">
            <div className="flex items-center gap-1.5">
              <Briefcase className="h-4 w-4" />
              <span>{selectedJob.title}</span>
              {selectedJob.status !== 'open' && (
                <span
                  className={`ml-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${STATUS_COLORS[selectedJob.status as JobStatus] ?? ''}`}
                >
                  {STATUS_LABELS[selectedJob.status as JobStatus] ?? selectedJob.status}
                </span>
              )}
            </div>
            {selectedJob.company && (
              <>
                <span className="text-gray-300">|</span>
                <span>{selectedJob.company.name}</span>
              </>
            )}
            <span className="text-gray-300">|</span>
            <div className="flex items-center gap-1.5">
              <Users className="h-4 w-4" />
              <span>
                {totalApplications} {totalApplications === 1 ? 'candidate' : 'candidates'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Top Matches Panel */}
      {selectedJobId && selectedJob && (
        <TopMatchesPanel
          jobId={selectedJobId}
          jobTitle={selectedJob.title}
        />
      )}

      {/* Kanban Board */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {!selectedJobId ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-text-primary">Select a Job</h3>
              <p className="text-text-tertiary mt-1">
                Choose a job from the dropdown to view its pipeline
              </p>
            </div>
          </div>
        ) : isLoadingPipeline ? (
          <div className="flex gap-4 h-full overflow-x-auto pb-4">
            {PIPELINE_STAGES.map((stage) => (
              <div key={stage} className="w-72 flex-shrink-0">
                <div className="flex items-center gap-2 mb-3 px-1">
                  <Skeleton className="w-3 h-3 rounded-full" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-5 w-5 rounded-full ml-auto" />
                </div>
                <div className="bg-bg-secondary rounded-lg p-2 h-[calc(100%-2rem)]">
                  <div className="space-y-2">
                    <Skeleton className="h-24 w-full" />
                    <Skeleton className="h-24 w-full" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : pipelineData ? (
          <KanbanBoard pipelineData={pipelineData} />
        ) : (
          <KanbanBoard pipelineData={createEmptyPipelineData(selectedJobId)} />
        )}
      </div>
    </div>
  );
}
